import { useId } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockRounded from '@mui/icons-material/LockRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import BackgroundSkeleton from './BackgroundSkeleton';

const styles = {
    root: (fullPage) => ({
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRadius: 2,
        minHeight: fullPage ? 'calc(100vh - 240px)' : { xs: 620, sm: 690 },
        height: fullPage ? 'calc(100vh - 240px)' : 'auto',
        width: '100%',
        px: { xs: 1, sm: 3 },
        py: fullPage ? { xs: 3, sm: 5 } : { xs: 4, sm: 10.5 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }),
    preview: {
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: 0.4,
        filter: 'blur(0.6px)'
    },
    panel: (theme) => ({
        width: '100%',
        maxWidth: 610,
        px: { xs: 2.5, sm: 5 },
        py: { xs: 3.5, sm: 4.5 },
        borderRadius: 3,
        textAlign: 'center',
        backgroundImage: 'none',
        boxShadow: `0 20px 48px ${alpha(theme.palette.common.black, 0.13)}`
    }),
    illustration: (theme) => ({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: 112, sm: 120 },
        height: { xs: 112, sm: 120 },
        mx: 'auto',
        mb: 2.5,
        borderRadius: '50%',
        bgcolor: alpha(theme.palette.secondary.main, 0.1),
        color: 'secondary.main',
        '& > svg': { fontSize: { xs: 60, sm: 72 }, width: '0.95em', height: '0.95em' }
    }),
    lock: {
        position: 'absolute',
        right: 16,
        bottom: 17,
        display: 'flex',
        p: 0.5,
        borderRadius: '50%',
        bgcolor: '#fff4d9',
        color: '#e5a000',
        '& svg': { fontSize: 36 }
    },
    title: { lineHeight: 1.3, fontWeight: 700, color: 'text.dark' },
    description: { mt: 1.5, lineHeight: 1.55, color: 'text.secondary' },
    actions: { width: '100%', maxWidth: 350, mx: 'auto', mt: 3.5 },
    button: { minHeight: 40, borderRadius: 2, fontSize: '1', fontWeight: 700, textTransform: 'none', px: 2 }
};

/**
 * Render a link or callback action without coupling the prompt to page routing.
 * @param {Object} props - Action configuration and button variant.
 * @returns {JSX.Element|null} Configured action button.
 */
function PlanAction({ action, variant }) {
    if (!action) return null;

    return (
        <Button
            type="button"
            variant={variant}
            color="secondary"
            fullWidth
            disableElevation
            className="main-font-b"
            href={action.href}
            onClick={action.onClick}
            target={action.external ? '_blank' : undefined}
            rel={action.external ? 'noopener noreferrer' : undefined}
            endIcon={action.external ? <OpenInNewRounded /> : undefined}
            sx={styles.button}
        >
            {action.label}
        </Button>
    );
}

/**
 * Show a non-dismissible upgrade panel while keeping application navigation usable.
 * Pass a decorative preview, never live restricted content, as the background.
 * @param {Object} props - Page-specific title, description, icon, preview, actions and optional content.
 * @returns {JSX.Element} Upgrade prompt contained within the current page.
 */
export default function PlanRestriction({ title, description, icon, primaryAction, secondaryAction, children, fullPage = false }) {
    const id = useId();

    return (
        <Box sx={styles.root(fullPage)}>
            <Box aria-hidden="true" inert="" sx={styles.preview}>
                <BackgroundSkeleton />
            </Box>
            <Paper
                role="dialog"
                aria-modal="false"
                aria-labelledby={`${id}-title`}
                aria-describedby={`${id}-description`}
                sx={styles.panel}
            >
                <Box aria-hidden="true" sx={styles.illustration}>
                    {icon || <LockRounded />}
                    {icon && (
                        <Box sx={styles.lock}>
                            <LockRounded />
                        </Box>
                    )}
                </Box>
                <Typography id={`${id}-title`} variant="h2" className="main-font-b" sx={styles.title}>
                    {title}
                </Typography>
                <Typography id={`${id}-description`} variant='body1' component="div" className="main-font" sx={styles.description}>
                    {description}
                </Typography>
                {children}
                {(primaryAction || secondaryAction) && (
                    <Stack spacing={1.5} sx={styles.actions}>
                        <PlanAction action={primaryAction} variant="contained" />
                        <PlanAction action={secondaryAction} variant="outlined" />
                    </Stack>
                )}
            </Paper>
        </Box>
    );
}

const actionPropType = PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    external: PropTypes.bool
});

PlanAction.propTypes = { action: actionPropType, variant: PropTypes.oneOf(['contained', 'outlined']).isRequired };

PlanRestriction.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.node.isRequired,
    icon: PropTypes.node,
    preview: PropTypes.node,
    primaryAction: actionPropType,
    secondaryAction: actionPropType,
    fullPage: PropTypes.bool,
    children: PropTypes.node
};
